import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin } from 'lucide-react';
import { useCMS } from '../hooks/useCMS';
import { useCompanyInfo } from '../contexts/CompanyInfoContext';
import TrustBar from './TrustBar';
import axios from 'axios';
import { getMediaUrl } from '../utils/mediaUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Footer = () => {
  const { get } = useCMS('footer');
  const companyInfo = useCompanyInfo();
  const [themeSettings, setThemeSettings] = useState(null);

  useEffect(() => {
    const fetchThemeSettings = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/theme-settings`);
        setThemeSettings(response.data);
      } catch (error) {
        // silent
      }
    };
    fetchThemeSettings();
  }, []);

  const getLogoUrl = () => {
    const darkModeLogo = themeSettings?.logos?.dark_mode;
    if (darkModeLogo) return getMediaUrl(darkModeLogo);
    return '/images/sand-tec-logo-light.png';
  };

  // Social media — only render icons that have URLs from company_info
  const socialMedia = companyInfo?.social_media || {};
  const socialLinks = [
    { key: 'facebook', url: socialMedia.facebook, icon: Facebook },
    { key: 'instagram', url: socialMedia.instagram, icon: Instagram },
    { key: 'linkedin', url: socialMedia.linkedin, icon: Linkedin },
  ].filter(s => s.url && s.url.trim() !== '');

  // Contact info from company_info (single source of truth)
  const contact = companyInfo?.contact || {};
  const address = companyInfo?.address || {};
  const phone = contact.phone || get('footer_phone', '');
  const email = contact.email || get('footer_email', '');
  const addressLine = `${address.street || ''}, ${address.postal_code || ''} ${address.city || ''}`.replace(/^,\s*/, '').trim();

  // Shortcuts mirror main navigation
  const quickLinks = [
    { label: 'Tjenester', to: '/tjenester' },
    { label: 'Aktuelt', to: '/aktuelt' },
    { label: 'Kampanjer', to: '/kampanjer' },
    { label: 'Vårt Team', to: '/team' },
    { label: 'Anmeldelser', to: '/anmeldelser' },
    { label: 'Referanser', to: '/referanser' },
    { label: 'Kontakt', to: '/kontakt' },
  ];

  return (
    <footer className="bg-black border-t border-gray-800 relative overflow-hidden" data-testid="site-footer">
      <TrustBar variant="compact" className="border-b border-gray-800" />
      <div className="h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent" />

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <img
              src={getLogoUrl()}
              alt={companyInfo?.company_name || 'Sand-tec AS'}
              className="h-16 w-auto mb-4"
              onError={(e) => { e.target.src = '/images/sand-tec-logo-light.png'; }}
            />
            <p className="text-gray-400 mb-4 max-w-md">
              {get('footer_description', 'Profesjonell EU-kontroll og service for tungbil, tilhenger og personbil. Moderne verksted med kapasitet for 6 store tilhengere samtidig.')}
            </p>
            {socialLinks.length > 0 && (
              <div className="flex space-x-4" data-testid="social-links">
                {socialLinks.map(({ key, url, icon: Icon }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-800 hover:bg-red-600 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
                    data-testid={`social-link-${key}`}
                    aria-label={key}
                  >
                    <Icon className="w-5 h-5 text-white" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold mb-4" data-testid="footer-quicklinks-title">
              {get('footer_quicklinks_title', 'Snarveier')}
            </h4>
            <ul className="space-y-2" data-testid="footer-quicklinks">
              {quickLinks.map(({ label, to }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-gray-400 hover:text-red-400 transition-colors duration-300"
                    data-testid={`footer-link-${to.slice(1)}`}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold mb-4">{get('footer_contact_title', 'Kontakt')}</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <a href={`tel:${phone}`} className="text-gray-400 hover:text-white transition-colors duration-300">
                  {phone}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <a href={`mailto:${email}`} className="text-gray-400 hover:text-white transition-colors duration-300">
                  {email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-400">{addressLine}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between">
          <p className="text-gray-500 text-sm mb-4 md:mb-0">
            {get('footer_copyright', `\u00A9 ${new Date().getFullYear()} ${companyInfo?.company_name || 'Sand-tec AS'}. Alle rettigheter reservert.`)}
          </p>
          <div className="flex space-x-6 text-sm">
            <Link to="/personvern" className="text-gray-500 hover:text-red-400 transition-colors duration-300">
              {get('footer_privacy', 'Personvern')}
            </Link>
            <Link to="/vilkar" className="text-gray-500 hover:text-red-400 transition-colors duration-300">
              {get('footer_terms', 'Vilkår')}
            </Link>
            <Link to="/cookies" className="text-gray-500 hover:text-red-400 transition-colors duration-300">
              {get('footer_cookies', 'Cookies')}
            </Link>
            <Link
              to="/admin/dashboard"
              className="text-gray-700 hover:text-red-400 transition-colors duration-300 opacity-50 hover:opacity-100"
              title="Admin Dashboard"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
